/**
 * Admin Incentives Panel - Manage badges, incentives, and view analytics
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  Trophy,
  Gift,
  Medal,
  Users,
  TrendingUp,
  DollarSign,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Zap,
  Flame,
  Target,
  Calendar,
  BarChart3,
  PieChart,
  RefreshCw,
  Coins
} from 'lucide-react';

// ============== ANALYTICS CARDS ==============

function AnalyticsCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Card className={`p-4 bg-slate-800/50 border-slate-700`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color.replace('text-', 'bg-')}/20 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </Card>
  );
}

// ============== BADGE MANAGEMENT ==============

function BadgeList({ badges, onToggle, onDelete }) {
  const tierColors = {
    bronze: 'border-amber-700',
    silver: 'border-slate-400',
    gold: 'border-yellow-400',
    platinum: 'border-purple-400'
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {badges.map(badge => (
        <Card 
          key={badge.id}
          className={`p-4 bg-slate-800/50 border-2 ${tierColors[badge.tier] || 'border-slate-700'} ${!badge.active ? 'opacity-50' : ''}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{badge.icon}</span>
              <div>
                <h4 className="font-semibold text-white">{badge.name}</h4>
                <p className="text-xs text-slate-400">{badge.description}</p>
              </div>
            </div>
            <Badge className={`${badge.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {badge.active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          
          <div className="mt-3 flex items-center gap-2 text-xs">
            <Badge className="bg-slate-700 text-slate-300">{badge.role}</Badge>
            <Badge className="bg-slate-700 text-slate-300">{badge.tier}</Badge>
            {badge.xp_reward > 0 && (
              <Badge className="bg-yellow-500/20 text-yellow-400">+{badge.xp_reward} XP</Badge>
            )}
            {badge.points_reward > 0 && (
              <Badge className="bg-green-500/20 text-green-400">+{badge.points_reward} pts</Badge>
            )}
          </div>
          
          <div className="mt-3 pt-3 border-t border-slate-700 flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Req: {Object.entries(badge.requirements || {}).map(([k, v]) => `${k.replace('min_', '')}: ${v}`).join(', ') || 'None'}
            </p>
            <div className="flex gap-1">
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-8 w-8"
                onClick={() => onToggle(badge.id, !badge.active)}
              >
                {badge.active ? 
                  <ToggleRight className="w-4 h-4 text-green-400" /> : 
                  <ToggleLeft className="w-4 h-4 text-slate-400" />
                }
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-8 w-8 text-red-400 hover:text-red-300"
                onClick={() => onDelete(badge.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ============== INCENTIVE LIST ==============

function IncentiveList({ incentives, onToggle }) {
  const typeColors = {
    badge: 'bg-purple-500/20 text-purple-400',
    streak: 'bg-orange-500/20 text-orange-400',
    bonus: 'bg-green-500/20 text-green-400',
    points: 'bg-yellow-500/20 text-yellow-400',
    referral: 'bg-blue-500/20 text-blue-400',
    mission: 'bg-pink-500/20 text-pink-400',
    xp: 'bg-amber-500/20 text-amber-400'
  };

  return (
    <div className="space-y-3">
      {incentives.map(inc => (
        <Card 
          key={inc.id}
          className={`p-4 bg-slate-800/50 border-slate-700 ${!inc.active ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                {inc.incentive_type === 'bonus' && <DollarSign className="w-5 h-5 text-green-400" />}
                {inc.incentive_type === 'xp' && <Zap className="w-5 h-5 text-yellow-400" />}
                {inc.incentive_type === 'streak' && <Flame className="w-5 h-5 text-orange-400" />}
                {inc.incentive_type === 'badge' && <Medal className="w-5 h-5 text-purple-400" />}
                {inc.incentive_type === 'points' && <Coins className="w-5 h-5 text-amber-400" />}
                {inc.incentive_type === 'mission' && <Target className="w-5 h-5 text-pink-400" />}
                {inc.incentive_type === 'referral' && <Users className="w-5 h-5 text-blue-400" />}
              </div>
              <div>
                <h4 className="font-semibold text-white">{inc.title}</h4>
                <p className="text-sm text-slate-400">{inc.description}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="flex gap-2">
                  <Badge className={typeColors[inc.incentive_type] || 'bg-slate-500/20 text-slate-400'}>
                    {inc.incentive_type}
                  </Badge>
                  <Badge className="bg-slate-700 text-slate-300">{inc.role}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Trigger: {inc.trigger} {inc.trigger_value ? `(${inc.trigger_value})` : ''}
                </p>
              </div>
              
              <div className="text-right min-w-[100px]">
                {inc.reward?.xp > 0 && <p className="text-sm text-yellow-400">+{inc.reward.xp} XP</p>}
                {inc.reward?.points > 0 && <p className="text-sm text-green-400">+{inc.reward.points} pts</p>}
                {inc.reward?.cash_bonus > 0 && <p className="text-sm text-emerald-400">+J${inc.reward.cash_bonus}</p>}
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{inc.total_claims || 0} claims</span>
                <Button 
                  size="sm" 
                  variant={inc.active ? "default" : "outline"}
                  onClick={() => onToggle(inc.id)}
                  className={inc.active ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {inc.active ? 'Active' : 'Inactive'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ============== CREATE INCENTIVE FORM ==============

function CreateIncentiveForm({ onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    incentive_type: 'bonus',
    role: 'both',
    trigger: 'delivery_completed',
    trigger_value: '',
    reward_xp: 0,
    reward_points: 0,
    reward_cash: 0,
    repeatable: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      title: formData.title,
      description: formData.description,
      incentive_type: formData.incentive_type,
      role: formData.role,
      trigger: formData.trigger,
      trigger_value: formData.trigger_value ? parseInt(formData.trigger_value) : null,
      reward: {
        xp: parseInt(formData.reward_xp) || 0,
        points: parseInt(formData.reward_points) || 0,
        cash_bonus: parseFloat(formData.reward_cash) || 0
      },
      repeatable: formData.repeatable
    });
  };

  return (
    <Card className="p-6 bg-slate-800 border-slate-700">
      <h3 className="text-lg font-semibold text-white mb-4">Create New Incentive</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">Title</Label>
            <Input 
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              placeholder="Incentive title"
              required
              className="bg-slate-900 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-300">Type</Label>
            <select 
              value={formData.incentive_type}
              onChange={e => setFormData({...formData, incentive_type: e.target.value})}
              className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white"
            >
              <option value="bonus">Cash Bonus</option>
              <option value="xp">XP Reward</option>
              <option value="points">Points</option>
              <option value="streak">Streak</option>
              <option value="mission">Mission</option>
              <option value="referral">Referral</option>
            </select>
          </div>
        </div>
        
        <div>
          <Label className="text-slate-300">Description</Label>
          <Input 
            value={formData.description}
            onChange={e => setFormData({...formData, description: e.target.value})}
            placeholder="Incentive description"
            required
            className="bg-slate-900 border-slate-700"
          />
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-300">Role</Label>
            <select 
              value={formData.role}
              onChange={e => setFormData({...formData, role: e.target.value})}
              className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white"
            >
              <option value="both">Both</option>
              <option value="driver">Driver</option>
              <option value="customer">Customer</option>
            </select>
          </div>
          <div>
            <Label className="text-slate-300">Trigger Event</Label>
            <select 
              value={formData.trigger}
              onChange={e => setFormData({...formData, trigger: e.target.value})}
              className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white"
            >
              <option value="delivery_completed">Delivery Completed</option>
              <option value="chain_leg_completed">Chain Leg Completed</option>
              <option value="rating_received">Rating Received</option>
              <option value="on_time_pickup">On-Time Pickup</option>
              <option value="purchase_made">Purchase Made</option>
              <option value="packages_sent">Packages Sent</option>
              <option value="referral_success">Referral Success</option>
              <option value="streak_days">Streak Days</option>
            </select>
          </div>
          <div>
            <Label className="text-slate-300">Trigger Value (optional)</Label>
            <Input 
              type="number"
              value={formData.trigger_value}
              onChange={e => setFormData({...formData, trigger_value: e.target.value})}
              placeholder="e.g., 10"
              className="bg-slate-900 border-slate-700"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-300">XP Reward</Label>
            <Input 
              type="number"
              value={formData.reward_xp}
              onChange={e => setFormData({...formData, reward_xp: e.target.value})}
              className="bg-slate-900 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-300">Points Reward</Label>
            <Input 
              type="number"
              value={formData.reward_points}
              onChange={e => setFormData({...formData, reward_points: e.target.value})}
              className="bg-slate-900 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-300">Cash Bonus (JMD)</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.reward_cash}
              onChange={e => setFormData({...formData, reward_cash: e.target.value})}
              className="bg-slate-900 border-slate-700"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <input 
            type="checkbox"
            id="repeatable"
            checked={formData.repeatable}
            onChange={e => setFormData({...formData, repeatable: e.target.checked})}
            className="rounded"
          />
          <Label htmlFor="repeatable" className="text-slate-300">Repeatable (can be earned multiple times)</Label>
        </div>
        
        <div className="flex gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" className="bg-green-600 hover:bg-green-700">Create Incentive</Button>
        </div>
      </form>
    </Card>
  );
}

// ============== MAIN COMPONENT ==============

export default function AdminIncentives() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [badges, setBadges] = useState([]);
  const [incentives, setIncentives] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [badgesRes, incentivesRes, analyticsRes] = await Promise.all([
        api.get('/gamification/badges'),
        api.get('/gamification/incentives/admin/all'),
        api.get('/gamification/analytics/summary')
      ]);
      
      setBadges(badgesRes.data.badges || []);
      setIncentives(incentivesRes.data.incentives || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load gamification data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBadge = async (badgeId, active) => {
    try {
      await api.patch(`/gamification/badges/${badgeId}`, { active });
      toast.success(`Badge ${active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update badge');
    }
  };

  const handleDeleteBadge = async (badgeId) => {
    if (!confirm('Are you sure you want to delete this badge?')) return;
    
    try {
      await api.delete(`/gamification/badges/${badgeId}`);
      toast.success('Badge deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete badge');
    }
  };

  const handleToggleIncentive = async (incentiveId) => {
    try {
      await api.patch(`/gamification/incentives/${incentiveId}/toggle`);
      toast.success('Incentive toggled');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle incentive');
    }
  };

  const handleCreateIncentive = async (data) => {
    try {
      await api.post('/gamification/incentives', data);
      toast.success('Incentive created');
      setShowCreateForm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create incentive');
    }
  };

  const handleSeedBadges = async () => {
    try {
      await api.post('/gamification/seed-badges');
      toast.success('Default badges seeded');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed badges');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading gamification data...</p>
        </div>
      </div>
    );
  }

  const driverStats = analytics?.by_role?.driver || {};
  const customerStats = analytics?.by_role?.customer || {};

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Incentive Control Panel
                </h1>
                <p className="text-sm text-slate-400">
                  Manage badges, incentives, and rewards
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSeedBadges}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset Badges
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowCreateForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Incentive
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-800 pb-4">
          {['overview', 'badges', 'incentives'].map(tab => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? 'bg-green-600' : ''}
            >
              {tab === 'overview' && <BarChart3 className="w-4 h-4 mr-2" />}
              {tab === 'badges' && <Medal className="w-4 h-4 mr-2" />}
              {tab === 'incentives' && <Gift className="w-4 h-4 mr-2" />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold">Analytics Overview</h2>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <AnalyticsCard 
                title="Total Drivers" 
                value={driverStats.total_users || 0}
                icon={Users}
                color="text-green-400"
              />
              <AnalyticsCard 
                title="Total Customers" 
                value={customerStats.total_users || 0}
                icon={Users}
                color="text-blue-400"
              />
              <AnalyticsCard 
                title="Total XP Awarded" 
                value={(driverStats.total_xp || 0) + (customerStats.total_xp || 0)}
                icon={Zap}
                color="text-yellow-400"
              />
              <AnalyticsCard 
                title="Cash Bonuses" 
                value={`J$${(analytics?.total_cash_bonuses || 0).toLocaleString()}`}
                icon={DollarSign}
                color="text-emerald-400"
              />
            </div>
            
            {/* Driver vs Customer Stats */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 bg-slate-800/50 border-slate-700">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    🚗
                  </div>
                  Driver Stats
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total XP</span>
                    <span className="text-yellow-400 font-medium">{(driverStats.total_xp || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Avg Level</span>
                    <span className="text-white font-medium">{(driverStats.avg_level || 0).toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Avg Streak</span>
                    <span className="text-orange-400 font-medium">{(driverStats.avg_streak || 0).toFixed(1)} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Badges Earned</span>
                    <span className="text-purple-400 font-medium">{driverStats.total_badges || 0}</span>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-slate-800/50 border-slate-700">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    👤
                  </div>
                  Customer Stats
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total Points</span>
                    <span className="text-yellow-400 font-medium">{(customerStats.total_points || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Avg Level</span>
                    <span className="text-white font-medium">{(customerStats.avg_level || 0).toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Avg Streak</span>
                    <span className="text-orange-400 font-medium">{(customerStats.avg_streak || 0).toFixed(1)} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Referrals Completed</span>
                    <span className="text-green-400 font-medium">{analytics?.total_referrals_completed || 0}</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Badges Tab */}
        {activeTab === 'badges' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Badge Management</h2>
              <span className="text-slate-400">{badges.length} badges</span>
            </div>
            
            <BadgeList 
              badges={badges}
              onToggle={handleToggleBadge}
              onDelete={handleDeleteBadge}
            />
          </div>
        )}

        {/* Incentives Tab */}
        {activeTab === 'incentives' && (
          <div className="space-y-6">
            {showCreateForm ? (
              <CreateIncentiveForm 
                onSubmit={handleCreateIncentive}
                onCancel={() => setShowCreateForm(false)}
              />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Active Incentives</h2>
                  <span className="text-slate-400">{incentives.length} incentives</span>
                </div>
                
                {incentives.length > 0 ? (
                  <IncentiveList 
                    incentives={incentives}
                    onToggle={handleToggleIncentive}
                  />
                ) : (
                  <Card className="p-8 bg-slate-800/50 border-slate-700 text-center">
                    <Gift className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No incentives created yet</p>
                    <Button 
                      className="mt-4 bg-green-600 hover:bg-green-700"
                      onClick={() => setShowCreateForm(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Incentive
                    </Button>
                  </Card>
                )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
